import { RefObject, useEffect, useRef } from "react"

export type ClickOutsideTarget = RefObject<HTMLElement | null> | (() => HTMLElement | null)

// MUI floating surfaces portal to `document.body`, so a click on them is "outside" the target in the DOM but
// still belongs to it. App-wide default so every consumer ignores them the same way.
export const MUI_PORTAL_SELECTOR = ".MuiModal-root, .MuiAutocomplete-popper, .MuiPickersPopper-root, .MuiTooltip-popper"

type ClickOutsideOptions = Readonly<{
    enabled?: boolean
    ignoreSelector?: string
    eventType?: "mousedown" | "mouseup" | "click"
    capture?: boolean
}>

// Runs `onOutside` when the user clicks outside `target` (a ref or an element getter), skipping clicks inside
// `ignoreSelector`. Generic and unaware of what the click triggers — closing popovers, leaving an edit, etc.
export function useClickOutside(
    target: ClickOutsideTarget,
    onOutside: (event: MouseEvent) => void,
    options?: ClickOutsideOptions,
) {
    const {
        enabled = true,
        ignoreSelector = MUI_PORTAL_SELECTOR,
        eventType = "mousedown",
        capture = true,
    } = options ?? {}

    const targetRef = useRef(target)
    targetRef.current = target
    const onOutsideRef = useRef(onOutside)
    onOutsideRef.current = onOutside

    useEffect(() => {
        if (!enabled) return undefined

        const handleEvent = (event: MouseEvent) => {
            const clickTarget = event.target
            if (!(clickTarget instanceof Element)) return
            if (ignoreSelector && clickTarget.closest(ignoreSelector)) return

            const currentTarget = targetRef.current
            const element = typeof currentTarget === "function" ? currentTarget() : currentTarget.current
            if (element && element.contains(clickTarget)) return

            onOutsideRef.current(event)
        }

        document.addEventListener(eventType, handleEvent, capture)
        return () => document.removeEventListener(eventType, handleEvent, capture)
    }, [enabled, ignoreSelector, eventType, capture])
}
